import { apiRequest } from '../../services/apiClient';
import { OrderStatus, WorkStatus } from '../../types/order';
import { PaymentTerms, ShippingMethod } from '../../types/orderPolicy';
import { RequestType } from '../Quote/quoteInquiryService';
import { DesignType } from '../Order/orderMenu';
import { AdminProfile, AdminRole, AdminStatus } from './adminSession';
import { PortfolioCategory, PortfolioItem, PortfolioItemInput } from '../Portfolio/portfolio.types';
import { AdminChatbotEntry, ChatbotEntryInput } from '../../components/chatbot/chatbotService';
import { AdminWorksItem, WorksItemInput } from '../Works/worksService';
import { AdminCertification, CertificationInput } from '../Company/certificationService';
import { CompanyHistoryContent } from '../Company/companyHistoryService';
import { AdminPartner, PartnerInput } from '../Company/partnerService';

function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface AdminOrderSummary {
  id: string;
  order_no: string;
  customer_name: string;
  phone: string;
  status: OrderStatus;
  request_type: RequestType;
  design_type: DesignType | null;
  work_status: WorkStatus;
  work_started_at: string | null;
  work_completed_at: string | null;
  title: string | null;
  company: string | null;
  shape: string | null;
  box_size: string | null;
  shipping_method: ShippingMethod;
  payment_terms: PaymentTerms;
  paid_at: string | null;
  quantity: number;
  unit_price: number | null;
  total_price: number | null;
  created_at: string;
  updated_at: string;
}

// requestType을 주면 견적문의(INQUIRY) / 주문관리(ORDER) 메뉴별로 나눠 조회한다
export function fetchAdminOrders(token: string, requestType?: RequestType): Promise<AdminOrderSummary[]> {
  return apiRequest<AdminOrderSummary[]>(`/api/admin/orders${requestType ? `?type=${requestType}` : ''}`, {
    headers: authHeaders(token),
  });
}

export interface AdminOrderNotificationItem {
  id: string;
  order_no: string;
  customer_name: string;
  title: string | null;
  shape: string | null;
  request_type: RequestType;
  created_at: string;
}

export interface AdminOrderNotifications {
  // DB 기준 현재 시각. 다음 조회 때 since로 그대로 넘긴다
  now: string;
  orders: AdminOrderNotificationItem[];
  pendingInquiries: number;
  pendingOrders: number;
}

export function fetchAdminOrderNotifications(token: string, since?: string): Promise<AdminOrderNotifications> {
  const query = since ? `?since=${encodeURIComponent(since)}` : '';
  return apiRequest<AdminOrderNotifications>(`/api/admin/orders/notifications${query}`, {
    headers: authHeaders(token),
  });
}

export function convertInquiryToOrder(orderId: string, token: string): Promise<{ requestType: RequestType }> {
  return apiRequest(`/api/admin/orders/${orderId}/convert`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });
}

export function updateOrderPaymentTerms(
  orderId: string,
  paymentTerms: PaymentTerms,
  paymentDueDate: string | null,
  token: string,
): Promise<{ paymentTerms: PaymentTerms; paymentDueDate: string | null }> {
  return apiRequest(`/api/admin/orders/${orderId}/payment-terms`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ paymentTerms, paymentDueDate }),
  });
}

export interface AdminOrderAttachment {
  id: number;
  file_path: string;
  file_name: string;
}

export interface AdminOrderDetail extends AdminOrderSummary {
  email: string | null;
  content_size: string | null;
  content_type: string | null;
  deadline: string | null;
  budget: string | null;
  print_option: string | null;
  route: string | null;
  content: string | null;
  reference_portfolio: string | null;
  carrier: string | null;
  tracking_number: string | null;
  revision_fee: number;
  payment_due_date: string | null;
  paid_amount: number | null;
  attachments: AdminOrderAttachment[];
}

export function fetchAdminOrderDetail(id: string, token: string): Promise<AdminOrderDetail> {
  return apiRequest<AdminOrderDetail>(`/api/admin/orders/${id}`, {
    headers: authHeaders(token),
  });
}

export interface OrderWorkStatusResult {
  work_status: WorkStatus;
  work_started_at: string | null;
  work_completed_at: string | null;
}

export function updateOrderWorkStatus(
  orderId: string,
  workStatus: WorkStatus,
  token: string,
): Promise<OrderWorkStatusResult> {
  return apiRequest<OrderWorkStatusResult>(`/api/admin/orders/${orderId}/work-status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ workStatus }),
  });
}

export interface OrderPriceResult {
  totalPrice: number;
  revisionFee: number;
}

export function updateOrderPrice(orderId: string, quotePrice: number, token: string): Promise<OrderPriceResult> {
  return apiRequest<OrderPriceResult>(`/api/admin/orders/${orderId}/price`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ quotePrice }),
  });
}

export interface OrderShippingResult {
  carrier: string;
  trackingNumber: string;
  status: OrderStatus;
}

export function updateOrderShipping(
  orderId: string,
  carrier: string,
  trackingNumber: string,
  token: string,
): Promise<OrderShippingResult> {
  return apiRequest<OrderShippingResult>(`/api/admin/orders/${orderId}/shipping`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ carrier, trackingNumber }),
  });
}

export interface LoginAdminResult {
  token: string;
  admin: AdminProfile;
}

export function loginAdmin(username: string, password: string): Promise<LoginAdminResult> {
  return apiRequest<LoginAdminResult>('/api/admin/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
}

export interface CreateAdminAccountInput {
  username: string;
  password: string;
  name?: string;
  email?: string;
  role?: AdminRole;
}

export interface CreateAdminAccountResult {
  id: number;
  username: string;
  name: string | null;
  email: string | null;
  role: AdminRole;
  status: AdminStatus;
}

export function createAdminAccount(
  token: string | null,
  input: CreateAdminAccountInput,
): Promise<CreateAdminAccountResult> {
  return apiRequest<CreateAdminAccountResult>('/api/admin/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(input),
  });
}

export interface UploadAdminProofResult {
  fileUrl: string;
  fileType: string;
  version: number;
}

export function uploadAdminProof(orderId: string, file: File, token: string): Promise<UploadAdminProofResult> {
  const formData = new FormData();
  formData.append('proofFile', file);

  return apiRequest<UploadAdminProofResult>(`/api/orders/${orderId}/proof`, {
    method: 'POST',
    headers: authHeaders(token),
    body: formData,
  });
}

export interface AdminProofMessageResult {
  sender: 'ADMIN';
  message: string;
  attachmentPath: string | null;
}

export function sendAdminProofMessage(
  orderId: string,
  message: string,
  token: string,
): Promise<AdminProofMessageResult> {
  const formData = new FormData();
  formData.append('message', message);

  return apiRequest<AdminProofMessageResult>(`/api/orders/${orderId}/proof/messages`, {
    method: 'POST',
    headers: authHeaders(token),
    body: formData,
  });
}

export interface AdminAccountSummary {
  id: number;
  username: string;
  name: string | null;
  email: string | null;
  role: AdminRole;
  status: AdminStatus;
  created_at: string;
}

export function fetchAdminAccounts(token: string): Promise<AdminAccountSummary[]> {
  return apiRequest<AdminAccountSummary[]>('/api/admin/admins', {
    headers: authHeaders(token),
  });
}

export function approveAdminAccount(token: string, id: number): Promise<{ id: number; status: AdminStatus }> {
  return apiRequest(`/api/admin/admins/${id}/approve`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });
}

export function updateAdminAccountRole(
  token: string,
  id: number,
  role: AdminRole,
): Promise<{ id: number; role: AdminRole }> {
  return apiRequest(`/api/admin/admins/${id}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ role }),
  });
}

export function deleteAdminAccount(token: string, id: number): Promise<{ id: number }> {
  return apiRequest(`/api/admin/admins/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminPortfolioItems(token: string): Promise<PortfolioItem[]> {
  return apiRequest<PortfolioItem[]>('/api/admin/portfolio', {
    headers: authHeaders(token),
  });
}

export function fetchAdminPortfolioItem(token: string, id: string): Promise<PortfolioItem> {
  return apiRequest<PortfolioItem>(`/api/admin/portfolio/${id}`, {
    headers: authHeaders(token),
  });
}

function buildPortfolioFormData(
  input: PortfolioItemInput,
  thumbnail?: File | null,
  removeThumbnail?: boolean,
): FormData {
  const formData = new FormData();
  formData.append('categoryId', input.categoryId);
  formData.append('brand', input.brand);
  formData.append('name', input.name);
  formData.append('tag', input.tag);
  formData.append('specTag', input.specTag);
  formData.append('price', input.price === null ? '' : String(input.price));
  formData.append('priceNote', input.priceNote);
  if (input.code) formData.append('code', input.code);
  formData.append('tone', String(input.tone));
  formData.append('deco', input.deco);
  formData.append('date', input.date);
  formData.append('popularity', String(input.popularity));
  formData.append('infoTag', input.infoTag);
  formData.append('subCopy', JSON.stringify(input.subCopy));
  formData.append('specs', JSON.stringify(input.specs));
  formData.append('processTitle', input.processTitle);
  formData.append('processLead', JSON.stringify(input.processLead));
  formData.append('reviews', JSON.stringify(input.reviews));
  formData.append('isPublished', String(input.isPublished));
  if (thumbnail) formData.append('thumbnail', thumbnail);
  if (removeThumbnail) formData.append('removeThumbnail', 'true');
  return formData;
}

export function createPortfolioItem(
  token: string,
  input: PortfolioItemInput,
  thumbnail: File | null,
): Promise<PortfolioItem> {
  return apiRequest<PortfolioItem>('/api/admin/portfolio', {
    method: 'POST',
    headers: authHeaders(token),
    body: buildPortfolioFormData(input, thumbnail),
  });
}

export function updatePortfolioItem(
  token: string,
  id: string,
  input: PortfolioItemInput,
  thumbnail: File | null,
  removeThumbnail: boolean,
): Promise<PortfolioItem> {
  return apiRequest<PortfolioItem>(`/api/admin/portfolio/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: buildPortfolioFormData(input, thumbnail, removeThumbnail),
  });
}

export function deletePortfolioItem(token: string, id: string): Promise<{ id: string }> {
  return apiRequest(`/api/admin/portfolio/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminPortfolioCategories(token: string): Promise<PortfolioCategory[]> {
  return apiRequest<PortfolioCategory[]>('/api/admin/portfolio-categories', {
    headers: authHeaders(token),
  });
}

export function createPortfolioCategory(
  token: string,
  input: { id?: string; label: string },
): Promise<PortfolioCategory> {
  return apiRequest<PortfolioCategory>('/api/admin/portfolio-categories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(input),
  });
}

export function updatePortfolioCategory(token: string, id: string, label: string): Promise<{ id: string; label: string }> {
  return apiRequest(`/api/admin/portfolio-categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ label }),
  });
}

export function reorderPortfolioCategories(token: string, ids: string[]): Promise<PortfolioCategory[]> {
  return apiRequest<PortfolioCategory[]>('/api/admin/portfolio-categories/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ ids }),
  });
}

export function deletePortfolioCategory(token: string, id: string): Promise<{ id: string }> {
  return apiRequest(`/api/admin/portfolio-categories/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminChatbotEntries(token: string): Promise<AdminChatbotEntry[]> {
  return apiRequest<AdminChatbotEntry[]>('/api/admin/chatbot', {
    headers: authHeaders(token),
  });
}

export function createChatbotEntry(token: string, input: ChatbotEntryInput): Promise<AdminChatbotEntry> {
  return apiRequest<AdminChatbotEntry>('/api/admin/chatbot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(input),
  });
}

export function updateChatbotEntry(token: string, id: number, input: ChatbotEntryInput): Promise<AdminChatbotEntry> {
  return apiRequest<AdminChatbotEntry>(`/api/admin/chatbot/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(input),
  });
}

export function reorderChatbotEntries(token: string, ids: number[]): Promise<AdminChatbotEntry[]> {
  return apiRequest<AdminChatbotEntry[]>('/api/admin/chatbot/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ ids }),
  });
}

export function deleteChatbotEntry(token: string, id: number): Promise<{ id: number }> {
  return apiRequest(`/api/admin/chatbot/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminWorks(token: string): Promise<AdminWorksItem[]> {
  return apiRequest<AdminWorksItem[]>('/api/admin/works', {
    headers: authHeaders(token),
  });
}

function buildWorksFormData(input: WorksItemInput, image: File | null): FormData {
  const formData = new FormData();
  formData.append('category', input.category);
  formData.append('title', input.title);
  formData.append('clientName', input.clientName);
  formData.append('description', input.description);
  formData.append('isPublished', String(input.isPublished));
  if (image) formData.append('image', image);
  return formData;
}

export function createWorksItem(token: string, input: WorksItemInput, image: File): Promise<AdminWorksItem> {
  return apiRequest<AdminWorksItem>('/api/admin/works', {
    method: 'POST',
    headers: authHeaders(token),
    body: buildWorksFormData(input, image),
  });
}

export function updateWorksItem(
  token: string,
  id: number,
  input: WorksItemInput,
  image: File | null,
): Promise<AdminWorksItem> {
  return apiRequest<AdminWorksItem>(`/api/admin/works/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: buildWorksFormData(input, image),
  });
}

export function reorderWorks(token: string, ids: number[]): Promise<AdminWorksItem[]> {
  return apiRequest<AdminWorksItem[]>('/api/admin/works/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ ids }),
  });
}

export function deleteWorksItem(token: string, id: number): Promise<{ id: number }> {
  return apiRequest(`/api/admin/works/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminCertifications(token: string): Promise<AdminCertification[]> {
  return apiRequest<AdminCertification[]>('/api/admin/certifications', {
    headers: authHeaders(token),
  });
}

function buildCertificationFormData(input: CertificationInput, image: File | null): FormData {
  const formData = new FormData();
  formData.append('title', input.title);
  formData.append('issuer', input.issuer);
  formData.append('issuedDate', input.issuedDate);
  formData.append('isPublished', String(input.isPublished));
  if (image) formData.append('image', image);
  return formData;
}

export function createCertification(
  token: string,
  input: CertificationInput,
  image: File,
): Promise<AdminCertification> {
  return apiRequest<AdminCertification>('/api/admin/certifications', {
    method: 'POST',
    headers: authHeaders(token),
    body: buildCertificationFormData(input, image),
  });
}

export function updateCertification(
  token: string,
  id: number,
  input: CertificationInput,
  image: File | null,
): Promise<AdminCertification> {
  return apiRequest<AdminCertification>(`/api/admin/certifications/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: buildCertificationFormData(input, image),
  });
}

export function reorderCertifications(token: string, ids: number[]): Promise<AdminCertification[]> {
  return apiRequest<AdminCertification[]>('/api/admin/certifications/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ ids }),
  });
}

export function deleteCertification(token: string, id: number): Promise<{ id: number }> {
  return apiRequest(`/api/admin/certifications/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminPartners(token: string): Promise<AdminPartner[]> {
  return apiRequest<AdminPartner[]>('/api/admin/partners', {
    headers: authHeaders(token),
  });
}

function buildPartnerFormData(input: PartnerInput, logo: File | null, removeLogo = false): FormData {
  const formData = new FormData();
  formData.append('name', input.name);
  formData.append('field', input.field);
  formData.append('url', input.url);
  formData.append('isPublished', String(input.isPublished));
  if (logo) formData.append('logo', logo);
  if (removeLogo) formData.append('removeLogo', 'true');
  return formData;
}

export function createPartner(token: string, input: PartnerInput, logo: File | null): Promise<AdminPartner> {
  return apiRequest<AdminPartner>('/api/admin/partners', {
    method: 'POST',
    headers: authHeaders(token),
    body: buildPartnerFormData(input, logo),
  });
}

export function updatePartner(
  token: string,
  id: number,
  input: PartnerInput,
  logo: File | null,
  removeLogo: boolean,
): Promise<AdminPartner> {
  return apiRequest<AdminPartner>(`/api/admin/partners/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: buildPartnerFormData(input, logo, removeLogo),
  });
}

export function reorderPartners(token: string, ids: number[]): Promise<AdminPartner[]> {
  return apiRequest<AdminPartner[]>('/api/admin/partners/order', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ ids }),
  });
}

export function deletePartner(token: string, id: number): Promise<{ id: number }> {
  return apiRequest(`/api/admin/partners/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
}

export function fetchAdminCompanyHistory(token: string): Promise<{ content: CompanyHistoryContent | null }> {
  return apiRequest<{ content: CompanyHistoryContent | null }>('/api/admin/company-history', {
    headers: authHeaders(token),
  });
}

export function saveCompanyHistory(
  token: string,
  content: CompanyHistoryContent,
): Promise<{ content: CompanyHistoryContent }> {
  return apiRequest<{ content: CompanyHistoryContent }>('/api/admin/company-history', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify(content),
  });
}
